import { createClient } from '@connectrpc/connect'
import { useMutation } from '@connectrpc/connect-query'
import { useQueryClient, useMutation as useTanstackMutation } from '@tanstack/react-query'
import _ from 'lodash'

import { getGetAlertQueryKey, getListAlertsQueryKey } from '@/api/alerts.hook'
import { getGetCompanyQueryKey, getListCompaniesQueryKey } from '@/api/company.hook'
import { BULK_MUTATION_CONCURRENCY } from '@/api/consts'
import { getGetControlQueryKey, getListControlsQueryKey } from '@/api/control.hook'
import {
  getGetSignalQueryKey,
  getGetSignalTimelineQueryKey,
  getListSignalsQueryKey,
} from '@/api/signals.hook'
import { getListCompaniesWithMostAlertsQueryKey } from '@/api/statistics.hook'
import { AlertStatus } from '@/gen/inventory/v1/alert_pb'
import {
  updateAlertStatus,
  updateManualSeverity,
} from '@/gen/inventory/v1/alert_service-AlertService_connectquery'
import { AlertService } from '@/gen/inventory/v1/alert_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { alertStatusLabel, riskLevelLabel } from '@/const/label'

import { settleWithConcurrencyLimit } from '@/lib/collection/async-utils'
import { defaultTransport } from '@/lib/connect/connect-transport'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

const alertClient = createClient(AlertService, defaultTransport)

export const useUpdateAlertStatus = (alertId: string) => {
  const { showUpdateAlertToast } = useUpdateAlertToast()
  const { invalidateAlertQueries } = useInvalidateAlertQueries(alertId)

  return useMutation(updateAlertStatus, {
    onSuccess: (_, { status }) => {
      invalidateAlertQueries()
      if (status !== undefined) showUpdateAlertToast('work status', alertStatusLabel[status])
    },
  })
}
export const useUpdateManualSeverity = (alertId: string) => {
  const { showUpdateAlertToast } = useUpdateAlertToast()
  const { invalidateAlertQueries } = useInvalidateAlertQueries(alertId)

  return useMutation(updateManualSeverity, {
    onSuccess: (_, { severity }) => {
      invalidateAlertQueries()
      if (severity) showUpdateAlertToast('severity', riskLevelLabel[severity])
    },
  })
}

const useUpdateAlertToast = () => {
  const { toast } = useToast()

  const showUpdateAlertToast = (entity: 'work status' | 'severity', newValue: string) => {
    if (newValue) {
      toast({
        title: `${_.capitalize(entity)} successfully changed`,
        description: `This alert's ${entity} has been set to '${newValue}'`,
        variant: 'success',
      })
    }
  }
  return { showUpdateAlertToast }
}

const useInvalidateAlertQueries = (alertId?: string) => {
  const queryClient = useQueryClient()
  const listCompaniesQueryKey = getListCompaniesQueryKey()
  const getCompanyQueryKey = getGetCompanyQueryKey()
  const listAlertsQueryKey = getListAlertsQueryKey()
  const listSignalsQueryKey = getListSignalsQueryKey()
  const getSignalQueryKey = getGetSignalQueryKey(alertId)
  const getSignalTimelineKey = getGetSignalTimelineQueryKey(alertId)
  const getControlKey = getGetControlQueryKey(alertId)
  const listControlsKey = getListControlsQueryKey()
  const listCompaniesWithMostAlertsKey = getListCompaniesWithMostAlertsQueryKey()
  const getAlertQueryKey = getGetAlertQueryKey()

  const invalidateAlertQueries = () => {
    queryClient.invalidateQueries({ queryKey: listCompaniesQueryKey })
    queryClient.invalidateQueries({ queryKey: getCompanyQueryKey })
    queryClient.removeQueries({ type: 'inactive' })
    queryClient.invalidateQueries({ queryKey: listAlertsQueryKey })
    queryClient.invalidateQueries({ queryKey: listSignalsQueryKey })
    queryClient.invalidateQueries({ queryKey: getSignalQueryKey })
    queryClient.invalidateQueries({ queryKey: getSignalTimelineKey })
    queryClient.invalidateQueries({ queryKey: getControlKey })
    queryClient.invalidateQueries({ queryKey: listControlsKey })
    queryClient.invalidateQueries({ queryKey: listCompaniesWithMostAlertsKey })
    queryClient.invalidateQueries({ queryKey: getAlertQueryKey })
  }

  return { invalidateAlertQueries }
}

const useBulkInvalidateAlertListQueries = () => {
  const queryClient = useQueryClient()

  return (ids: string[]) => {
    queryClient.invalidateQueries({ queryKey: getListAlertsQueryKey() })
    for (const id of ids) {
      queryClient.invalidateQueries({ queryKey: getGetSignalQueryKey(id) })
      queryClient.invalidateQueries({ queryKey: getGetSignalTimelineQueryKey(id) })
    }
  }
}

export const useBulkUpdateAlertStatus = () => {
  const invalidateAlertQueries = useBulkInvalidateAlertListQueries()

  return useTanstackMutation({
    mutationFn: ({
      ids,
      status,
      comment,
    }: {
      ids: string[]
      status: AlertStatus
      comment?: string
    }) =>
      settleWithConcurrencyLimit(
        ids,
        (id) => alertClient.updateAlertStatus({ id, status, comment }),
        BULK_MUTATION_CONCURRENCY,
      ),
    onSettled: (_data, _error, { ids }) => invalidateAlertQueries(ids),
  })
}

export const useBulkUpdateAlertSeverity = () => {
  const invalidateAlertQueries = useBulkInvalidateAlertListQueries()

  return useTanstackMutation({
    mutationFn: ({
      ids,
      severity,
      comment,
    }: {
      ids: string[]
      severity: RiskLevel
      comment?: string
    }) =>
      settleWithConcurrencyLimit(
        ids,
        (id) => alertClient.updateManualSeverity({ id, severity, comment }),
        BULK_MUTATION_CONCURRENCY,
      ),
    onSettled: (_data, _error, { ids }) => invalidateAlertQueries(ids),
  })
}
